import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Plus, Edit, Trash2, ArrowLeft, Copy, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { supabase } from '../lib/supabase';
import { useAppStore } from '../store';
import { useToast } from '../hooks/useToast';
import { formatArgentinaDate } from '../utils/dateUtils';

interface CalendarEvent {
  id: string;
  workshop_id: string;
  production_id?: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  color: string;
  created_at: string;
}

interface Production {
  id: string;
  name: string;
  client_id: string;
}

const WorkshopCalendarView: React.FC = () => {
  const { workshopId } = useParams<{ workshopId: string }>();
  const navigate = useNavigate();
  const { suppliers, productions, clients } = useAppStore();
  const { toast, ToastComponent } = useToast();

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [copiedLink, setCopiedLink] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    production_id: '',
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    color: '#3b82f6'
  });

  const workshop = suppliers.find(s => s.id === workshopId);

  useEffect(() => {
    if (workshopId) {
      loadEvents();
    }
  }, [workshopId]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('workshop_calendar_events')
        .select('*')
        .eq('workshop_id', workshopId)
        .order('start_date', { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error: any) {
      console.error('Error loading events:', error);
      toast.error('Error', 'No se pudieron cargar los eventos');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEvent = async () => {
    try {
      if (!formData.title || !formData.start_date || !formData.end_date) {
        toast.error('Error', 'Por favor completa todos los campos requeridos');
        return;
      }

      const { data: userData } = await supabase.auth.getUser();

      const eventData = {
        workshop_id: workshopId,
        production_id: formData.production_id || null,
        title: formData.title,
        description: formData.description,
        start_date: formData.start_date,
        end_date: formData.end_date,
        color: formData.color,
        created_by: userData.user?.id
      };

      if (editingEvent) {
        const { error } = await supabase
          .from('workshop_calendar_events')
          .update(eventData)
          .eq('id', editingEvent.id);

        if (error) throw error;
        toast.success('Evento actualizado');
      } else {
        const { error } = await supabase
          .from('workshop_calendar_events')
          .insert(eventData);

        if (error) throw error;
        toast.success('Evento creado');
      }

      setIsModalOpen(false);
      setEditingEvent(null);
      resetForm();
      loadEvents();
    } catch (error: any) {
      console.error('Error saving event:', error);
      toast.error('Error', 'No se pudo guardar el evento');
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      const { error } = await supabase
        .from('workshop_calendar_events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;
      toast.success('Evento eliminado');
      setDeleteConfirmModal(null);
      setIsModalOpen(false);
      setEditingEvent(null);
      loadEvents();
    } catch (error: any) {
      console.error('Error deleting event:', error);
      toast.error('Error', 'No se pudo eliminar el evento');
    }
  };

  const handleCopyPublicLink = async () => {
    try {
      const { data, error } = await supabase.rpc('get_or_create_workshop_link', {
        p_workshop_id: workshopId
      });

      if (error) throw error;

      const publicUrl = `${window.location.origin}/calendario-publico/${data}`;
      await navigator.clipboard.writeText(publicUrl);

      setCopiedLink(true);
      toast.success('Link copiado', 'El link público ha sido copiado al portapapeles');

      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error: any) {
      console.error('Error copying public link:', error);
      toast.error('Error', 'No se pudo copiar el link público');
    }
  };

  const openModal = (event?: CalendarEvent) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        production_id: event.production_id || '',
        title: event.title,
        description: event.description,
        start_date: event.start_date,
        end_date: event.end_date,
        color: event.color
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      production_id: '',
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      color: '#3b82f6'
    });
    setEditingEvent(null);
  };

  const handleProductionChange = (productionId: string) => {
    setFormData(prev => ({ ...prev, production_id: productionId }));

    if (productionId) {
      const production = productions.find(p => p.id === productionId);
      if (production && !formData.title) {
        const client = clients.find(c => c.id === production.client_id);
        setFormData(prev => ({
          ...prev,
          title: `${production.name}${client ? ` - ${client.name}` : ''}`
        }));
      }
    }
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const monthEvents = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    return events.filter(event => {
      const eventStart = new Date(event.start_date);
      const eventEnd = new Date(event.end_date);
      return (eventStart <= lastDay && eventEnd >= firstDay);
    });
  }, [events, currentDate]);

  const getEventsForDate = (date: Date) => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    return events.filter(event => {
      const eventStart = new Date(event.start_date + 'T00:00:00');
      const eventEnd = new Date(event.end_date + 'T00:00:00');
      const eventStartOnly = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate());
      const eventEndOnly = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());

      return dateOnly >= eventStartOnly && dateOnly <= eventEndOnly;
    });
  };

  const renderCalendar = () => {
    const days = [];
    const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const startDate = new Date(yesterday);
    const DAYS_TO_SHOW = 30;

    const startDayOfWeek = startDate.getDay();

    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-gray-50 dark:bg-gray-800"></div>);
    }

    for (let i = 0; i < DAYS_TO_SHOW; i++) {
      const currentDay = new Date(startDate);
      currentDay.setDate(startDate.getDate() + i);

      const dayEvents = getEventsForDate(currentDay);

      const isToday = today.getFullYear() === currentDay.getFullYear() &&
                      today.getMonth() === currentDay.getMonth() &&
                      today.getDate() === currentDay.getDate();

      const isYesterday = yesterday.getFullYear() === currentDay.getFullYear() &&
                          yesterday.getMonth() === currentDay.getMonth() &&
                          yesterday.getDate() === currentDay.getDate();

      const dayNumber = currentDay.getDate();
      const monthName = currentDay.toLocaleDateString('es-ES', { month: 'short' });
      const isNewMonth = i > 0 && currentDay.getDate() === 1;

      days.push(
        <div
          key={`${currentDay.getFullYear()}-${currentDay.getMonth()}-${currentDay.getDate()}`}
          className={`min-h-24 p-1 border border-gray-200 dark:border-gray-700 ${
            isToday ? 'bg-blue-50 dark:bg-blue-900/20' :
            isYesterday ? 'bg-gray-100 dark:bg-gray-800' :
            'bg-white dark:bg-gray-900'
          }`}
        >
          <div className={`text-xs font-semibold ${
            isToday ? 'text-blue-600' :
            isYesterday ? 'text-gray-500 dark:text-gray-400' :
            'text-gray-600 dark:text-gray-400'
          } mb-1`}>
            {isNewMonth ? `${dayNumber} ${monthName}` : dayNumber}
          </div>
          <div className="space-y-1">
            {dayEvents.slice(0, 3).map(event => (
              <div
                key={event.id}
                className="text-xs p-1 rounded cursor-pointer hover:opacity-80 transition-opacity"
                style={{ backgroundColor: event.color + '20', borderLeft: `3px solid ${event.color}` }}
                onClick={() => openModal(event)}
                title={event.title}
              >
                <div className="truncate font-medium">{event.title}</div>
              </div>
            ))}
            {dayEvents.length > 3 && (
              <div className="text-xs text-gray-500 dark:text-gray-400 pl-1">
                +{dayEvents.length - 3} más
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="grid grid-cols-7 gap-0">
          {weekDays.map(day => (
            <div key={day} className="text-center text-sm font-semibold text-gray-600 dark:text-gray-400 py-2">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0 border border-gray-200 dark:border-gray-700">
          {days}
        </div>
      </div>
    );
  };

  if (!workshop) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-400">Taller no encontrado</p>
        <Button onClick={() => navigate('/calendario-talleres')} className="mt-4">
          Volver
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {ToastComponent}

      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/calendario-talleres')}
          >
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <Calendar className="h-8 w-8 mr-3 text-blue-600" />
              {workshop.name}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Calendario de producciones
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            icon={copiedLink ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            onClick={handleCopyPublicLink}
          >
            {copiedLink ? 'Copiado' : 'Copiar Link Público'}
          </Button>
          <Button
            icon={<Plus className="h-5 w-5" />}
            onClick={() => openModal()}
          >
            Calendarizar Producción
          </Button>
        </div>
      </div>

      <Card>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-gray-500">Cargando calendario...</div>
          ) : (
            renderCalendar()
          )}
        </CardContent>
      </Card>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingEvent ? 'Editar Evento' : 'Calendarizar Producción'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Producción (opcional)
                </label>
                <select
                  value={formData.production_id}
                  onChange={(e) => handleProductionChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Sin producción asociada</option>
                  {productions.map(prod => {
                    const client = clients.find(c => c.id === prod.client_id);
                    return (
                      <option key={prod.id} value={prod.id}>
                        {prod.name} {client ? `- ${client.name}` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>

              <Input
                label="Título *"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ej: Remeras Jean Crudo - Cliente XYZ"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={3}
                  placeholder="Detalles adicionales..."
                />
              </div>

              <Input
                label="Fecha Inicio *"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
              />

              <Input
                label="Fecha Fin *"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Color
                </label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-full h-10 rounded-md cursor-pointer"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              {editingEvent && (
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirmModal(editingEvent.id)}
                  icon={<Trash2 className="h-4 w-4" />}
                >
                  Eliminar
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button onClick={handleSaveEvent}>
                {editingEvent ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Confirmar eliminación
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              ¿Estás seguro de que deseas eliminar esta producción del calendario? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmModal(null)}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => handleDeleteEvent(deleteConfirmModal)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Eliminar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkshopCalendarView;
